import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import { ICON_BUTTON_SIZES, ICON_BUTTON_VARIANT } from './constants';

// Design System — IconButton
// Circular icon-only button sized to match Avatar (dashed neutral circle — the
// "add user" affordance by default; pass any icon name for other uses). Wrapped
// in the core Tooltip (hover) when `tooltip` is set.
// `variant="plain"` drops the circle and the control-size box and leaves the
// glyph alone, sized from font-size (`size` has no effect on it) — for
// close/remove/toggle controls whose colour and geometry belong to the call site;
// its rules carry zero specificity, so the call site's stylesheet always wins.
// `label` is the accessible name — pass it already translated (e.g. t('Add
// user')); the raw icon slug is never used as a label. Falls back to `tooltip`.
// Styles: ./index.less is wired into less/ats_style.less (white-label pipeline).

const IconButton = ({
    icon = 'plus',
    label = null,
    size = 'md',
    variant = ICON_BUTTON_VARIANT.DASHED,
    tooltip = null,
    onClick = null,
    disabled = false,
    className = null,
    // Extra native attributes (data-action, id, …) pass straight through
    ...rest
}) => {
    const button = (
        <button
            type="button"
            className={classNames(
                'icon-button',
                ICON_BUTTON_SIZES.includes(size) ? `icon-button--${size}` : 'icon-button--md',
                { 'icon-button--plain': variant === ICON_BUTTON_VARIANT.PLAIN },
                className
            )}
            onClick={typeof onClick === 'function' ? onClick : undefined}
            disabled={disabled}
            aria-label={label || tooltip || undefined}
            {...rest}
        >
            <Icon value={icon} noMargin />
        </button>
    );

    if (!tooltip) return button;
    return <Tooltip title={tooltip}>{button}</Tooltip>;
};

// A human-readable accessible name must come from `label` or `tooltip`.
const requireAccessibleName = (props, propName, componentName) => {
    if (!props.label && !props.tooltip) {
        return new Error(`${componentName}: provide a translated \`label\` or \`tooltip\` for the accessible name.`);
    }
    if (props[propName] != null && typeof props[propName] !== 'string') {
        return new Error(`${componentName}: \`${propName}\` must be a string.`);
    }
    return null;
};

IconButton.propTypes = {
    icon: PropTypes.string,
    label: requireAccessibleName,
    size: PropTypes.oneOf(ICON_BUTTON_SIZES),
    /** `dashed` (default) is the circular dashed control; `plain` is the bare glyph */
    variant: PropTypes.oneOf(Object.values(ICON_BUTTON_VARIANT)),
    tooltip: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    className: PropTypes.string,
};

export { ICON_BUTTON_SIZES, ICON_BUTTON_VARIANT };
export default IconButton;
